import cx from 'classnames'
import type { RefObject } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import useKeyboardShortcuts, {
  defaultWithInputOnScreen,
} from '@src/lib/hooks/useKeyboardShortcuts'

import * as styles from './ChipInput.css'

interface RenderChipStates {
  focused: boolean
}

interface Chip {
  id: string
}

export interface ChipInputProps<T extends Chip>
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  inputRef: RefObject<HTMLInputElement>
  size: 24 | 30
  chips: T[]
  onDelete?: (index: number) => void
  onEnter?: (value: string) => void
  renderChip?: (value: T, states: RenderChipStates) => React.ReactNode
}

/**
 * @deprecated
 */
const ChipInput = <T extends Chip>({
  value,
  size,
  chips,
  renderChip,
  onBlur,
  onChange,
  onEnter,
  onDelete,
  placeholder,
  className,
  inputRef,
  ...props
}: ChipInputProps<T>) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const shadowRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number>(5)
  const [focusedChip, setFocusedChip] = useState<number | null>(null)

  useLayoutEffect(() => {
    setWidth(shadowRef.current ? shadowRef.current.getBoundingClientRect().width + 5 : 5)
  }, [value, chips])

  useEffect(() => {
    if (focusedChip && focusedChip > chips.length - 1) {
      setFocusedChip(null)
    }
  }, [chips, focusedChip])

  useKeyboardShortcuts({
    node: inputRef,
    name: 'chip-input',
    filter: defaultWithInputOnScreen({
      allowedShortcutsAlways: ['Enter'],
      allowedShortcutsWhenEmpty: ['Delete', 'Backspace', 'ArrowLeft', 'ArrowRight'],
    }),
    handler: (shortcut, event) => {
      if (shortcut === 'Enter' && inputRef.current?.value) {
        onEnter?.(inputRef.current.value)
        event.stopPropagation()
      }

      if (shortcut === 'Backspace' || shortcut === 'Delete') {
        const index = focusedChip ?? chips.length - 1
        if (index > -1) {
          onDelete?.(index)
        }
        event.stopPropagation()
      }

      if (shortcut === 'ArrowLeft') {
        setFocusedChip((h) => Math.max(0, h !== null ? h - 1 : chips.length - 1 || 0))
        event.stopPropagation()
      }

      if (shortcut === 'ArrowRight') {
        setFocusedChip((h) => (h === null || h >= chips.length - 1 ? null : h + 1))
        event.stopPropagation()
      }
    },
    dep: [chips, focusedChip],
  })

  const handleRender = (chip: T, index: number) => {
    return (
      <div key={chip.id} onClick={() => setFocusedChip(index)}>
        {renderChip?.(chip, {
          focused: index === focusedChip,
        })}
      </div>
    )
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFocusedChip(null)
    onChange?.(event)
  }

  const handleClick = () => {
    inputRef.current?.focus()
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocusedChip(null)
    onBlur?.(event)
  }

  return (
    <div ref={rootRef} onClick={handleClick} className={cx(styles.root, className)}>
      {chips.map(handleRender)}
      <input
        ref={inputRef}
        className={styles.input({ size })}
        style={{ width }}
        {...props}
        data-allow-keyboard-when-empty
        value={value}
        placeholder={chips.length > 0 ? undefined : placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <div ref={shadowRef} className={styles.shadow}>
        {value}
      </div>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-deprecated
export default ChipInput
